import axios, { type AxiosError } from "axios";
import { t } from "@lingui/core/macro";
import type { ApiErrorResponse } from "../types/apiError";
import { ERROR_CODES, isErrorCode } from "./errorCodes";

/**
 * The raw text of a failure: the server's message / error, axios's transport
 * text ("Request failed with status code 500", "Network Error") or a JS
 * exception's message. It is English engineering prose, so it is for the
 * CONSOLE ONLY - never rendered (SPEC-FRONTEND-RULE-088, D-318). `null` when
 * there is none.
 */
export function rawErrorText(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: unknown; error?: unknown; detail?: unknown }
      | undefined;
    for (const v of [data?.message, data?.error, data?.detail, error.message]) {
      if (typeof v === "string" && v) return v;
    }
    return null;
  }
  if (error instanceof Error) return error.message || null;
  if (typeof error === "string") return error || null;
  return null;
}

/**
 * The ONE thing a surface shows for a failure (SPEC-FRONTEND-RULE-088, D-318
 * as amended 2026-09-15: "the user sees ONLY the identifier"): the identifier
 * `HIBAKÓD #<display_code> · ref <request_id>` when the server answered with a
 * number or a reference, else the surface's own catalogued line (a network
 * failure, a runtime exception, or an answer with nothing quotable - a bare
 * "HIBAKÓD" label tells the user nothing). The raw server / axios / exception
 * text goes to console.warn only - never to the page, never a per-code sentence.
 */
export function describeFailure(error: unknown, noAnswerLine: string): string {
  const raw = rawErrorText(error);
  if (raw) console.warn("[error]", raw);
  const identifier = getErrorIdentifier(error);
  return identifier && identifier !== t`ERROR CODE` ? identifier : noAnswerLine;
}

/**
 * What a surface shows for a failed API call: `describeFailure(error, fallback)`.
 * It used to return the server's `message` / `error` or axios's English
 * transport text verbatim; it no longer renders any raw text (RULE-088).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  return describeFailure(error, fallback);
}

/**
 * Type guard — checks whether a response body matches the ApiErrorResponse shape.
 */
export function isApiError(error: unknown): error is AxiosError<ApiErrorResponse> {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data;
  return (
    data != null &&
    typeof data === "object" &&
    typeof (data as ApiErrorResponse).error === "string" &&
    typeof (data as ApiErrorResponse).code === "number" &&
    typeof (data as ApiErrorResponse).message === "string"
  );
}

/**
 * Returns the full ApiErrorResponse from an error, or null if it doesn't match.
 */
export function getApiError(error: unknown): ApiErrorResponse | null {
  if (!isApiError(error)) return null;
  return error.response?.data ?? null;
}

/**
 * Extract the request_id from a VERISA API error response, if present.
 */
export function extractRequestId(error: unknown): string | null {
  // Read structurally, like getErrorIdentifier: any error carrying a response
  // (axios's, or a wrapper that kept it) quotes the same ref.
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (
    data != null &&
    typeof data === "object" &&
    typeof (data as ApiErrorResponse).request_id === "string"
  ) {
    return (data as ApiErrorResponse).request_id ?? null;
  }
  return null;
}

/**
 * What a load surface shows for a failure. D-UIT-02 (Tibor 2026-09-30, item 17,
 * amending D-318's "identifier only"): an ACTIONABLE client-side sentence may be
 * shown for a 4xx - reconnect (no answer), invalid request (400), sign in again
 * (401), no permission (403), not found (404), wait (429); the RULE-088
 * identifier stays for 5xx-like server faults and anything unmapped:
 * `describeFailure(error, "Failed to load <context>.")` - the identifier when the
 * server answered with a number or a ref, else that line. The raw text goes to
 * the console only. One identifier form: no "(ref: X)" suffix.
 *
 * @param error  - The caught error (Axios, Error, or unknown)
 * @param context - A catalogued noun for what was being fetched (t`sources`)
 */
export function getUserFriendlyErrorMessage(error: unknown, context: string): string {
  if (axios.isAxiosError(error) && !error.response) {
    return t`Unable to connect to server. Please check your connection.`;
  }
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  if (status === 400) return t`Invalid request for ${context}.`;
  if (status === 401) return t`Your session has expired. Please sign in again.`;
  if (status === 403) return t`You do not have permission to access ${context}.`;
  if (status === 404) return t`The requested ${context} could not be found.`;
  if (status === 429) return t`Too many requests. Please wait a moment and try again.`;
  if (status === undefined) {
    // No answer from a server: a runtime exception or an unknown throw.
    return describeFailure(error, t`An unexpected error occurred while loading ${context}.`);
  }
  return describeFailure(error, t`Failed to load ${context}.`);
}

/**
 * The SPEC-FRONTEND-RULE-088 identifier of a backend failure, and nothing else:
 * `ERROR CODE #<display_code> · ref <request_id>`, the label localised (HU
 * "HIBAKÓD"), the number and the ref verbatim. The number comes from the
 * envelope's details.display_code when the gateway sends one, else from the
 * shared error-code registry by details.code or the envelope's error; without
 * either, the label and the ref alone - never the server's message, never blank.
 * `null` when there was no response at all: a network failure is not a backend
 * answer, and the caller renders its own typed message for it.
 *
 * The ONE builder of this identifier on the HTTP path, so every surface quotes
 * the same shape and the same catalogued label (RULE-088).
 */
export function getErrorIdentifier(error: unknown): string | null {
  const response = (error as { response?: { data?: unknown } } | null)?.response;
  if (!response) return null;
  const data = (response.data ?? {}) as Partial<ApiErrorResponse> & {
    details?: { display_code?: unknown; code?: unknown } | null;
  };
  return formatErrorIdentifier({
    displayCode: typeof data.details?.display_code === "number" ? data.details.display_code : undefined,
    code: typeof data.details?.code === "string" ? data.details.code : data.error,
    requestId: extractRequestId(error),
  });
}

/**
 * The RULE-088 identifier from its parts - for a failure that is not an HTTP
 * response (a chat stream's typed error frame): the number from displayCode,
 * else from the shared registry by code; the label and the ref alone without
 * either. Never blank, never a message.
 */
/**
 * describeFailure for a chat stream's typed error frame: its identifier when
 * the frame carries a number or a ref, else the surface's own line - never a
 * bare "HIBAKÓD" (a string frame, or one with neither, has nothing quotable).
 */
export function describeFrameFailure(
  parts: { displayCode?: number; code?: unknown; requestId?: string | null },
  noAnswerLine: string,
): string {
  const identifier = formatErrorIdentifier(parts);
  return identifier !== t`ERROR CODE` ? identifier : noAnswerLine;
}

export function formatErrorIdentifier(parts: {
  displayCode?: number;
  code?: unknown;
  requestId?: string | null;
}): string {
  let displayCode = parts.displayCode;
  if (displayCode === undefined && isErrorCode(parts.code)) {
    displayCode = ERROR_CODES[parts.code].displayCode;
  }
  const ref = parts.requestId || null;
  return [t`ERROR CODE`, displayCode !== undefined ? `#${displayCode}` : null, ref ? `· ref ${ref}` : null]
    .filter(Boolean)
    .join(" ");
}
